import { ElementRef, OnChanges, ViewChild, Output, EventEmitter, HostBinding, SimpleChanges, Component, signal, WritableSignal, input, Signal, computed, OnDestroy } from '@angular/core';
import { ArcElement, BarController, BarElement, CategoryScale, Chart, InteractionMode, LinearScale, LineController, LineElement, PieController, PointElement, Title, Tooltip, Filler } from 'chart.js';
import { AppService } from 'app/app.service';
import { BaseComponent } from 'app/shared/components';
import { takeUntil } from 'rxjs/operators';
import { Dataset, Legend, GraphPointSelection, LegendItem, DataLabelsFormatterContext, ChartConfigurationWithPlugins, DataPoint, ChartDataSetsWithPlugins, Label } from './graph.model';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import ChartAnnotation from 'chartjs-plugin-annotation';
import { THEME_GRAPH_PALETTES } from '../theme/theme-defaults';
import { get } from 'lodash';

@Component({
    selector: 'key-graph-base-component',
    template: '',
})
export abstract class KeyGraphBaseComponent extends BaseComponent implements OnChanges, OnDestroy {
    private redrawChangesList = new Set<string>(['stacked', 'yAxes', 'showLabelsOnGraph', 'type', 'fill', 'averages']);
    protected axesLabelColor: string;
    protected axesLabelFont: string;
    protected axesLabelSize: number;
    protected colors: string[];
    protected shouldRedrawChart: boolean;

    labels = input<Label[]>();
    datasets = input<Dataset[]>();
    legend = input<Legend>();

    chartType: Signal<string> = computed(() => this.datasets()[0]?.type || null);

    legendMinimized: WritableSignal<boolean> = signal(false);
    legendItems: WritableSignal<LegendItem[]> = signal([]);
    loaded: WritableSignal<boolean> = signal(false);

    // i.e. the labels physically on each of the barchart bars
    showLabelsOnGraph = input<boolean>();

    tooltipMode = input<'none' | 'single' | 'multiple'>('none');
    stacked = input<boolean>();

    @HostBinding('class') hostBaseClasses = 'position-relative d-flex flex-1';
    @HostBinding('class.flex-column') flexColumn: boolean;

    @Output() onPointSelection = new EventEmitter<GraphPointSelection>();
    @Output() onDatasetHidden = new EventEmitter<{ dataset: Dataset, hidden: boolean }>();

    @ViewChild('graph', { static: true }) chartContext!: ElementRef<HTMLCanvasElement>;
    @ViewChild('legendEl') legendEl: ElementRef;

    chart!: Chart;
    
    config: ChartConfigurationWithPlugins;

    tooltipModeMap: { [key: string]: { mode: InteractionMode, intersect: boolean } } = {
        none: { mode: 'nearest', intersect: false },
        single: { mode: 'index', intersect: true },
        multiple: { mode: 'index', intersect: true },
    };

    get baseConfig(): Partial<ChartConfigurationWithPlugins> {
        return {
            options: {
                animation: {
                    duration: 0,
                },
                responsive: true,
                maintainAspectRatio: false,
                onClick: this.handlePointsSelectionEvent.bind(this),
                onHover: (_, elements) => this.handleCursorOnHover(elements),
                plugins: {
                    legend: {
                        display: false,
                    },
                    tooltip: {
                        mode: this.tooltipModeMap[this.tooltipMode()].mode,
                        intersect: this.tooltipModeMap[this.tooltipMode()].intersect,
                        titleColor: 'rgba(255, 255, 255, .8)',
                        titleFont: {style: 'normal'},
                        position: 'nearest'
                    },
                    datalabels: {
                        display: this.showLabelsOnGraph() ? (this.stacked() || 'auto') : false,
                        anchor: this.stacked() ? 'center' : 'end',
                        clamp: true,
                        font: {
                            size: 10,
                        },
                        formatter: (value: number, context: DataLabelsFormatterContext): string | number => {
                            const dataset = this.datasets()[context.datasetIndex];
                            const dataValue = dataset.data[context.dataIndex].value;
                            return dataValue || value;
                        },
                    },
                },
            },
            plugins: [ChartDataLabels, {
                id: 'loadingCallback',
                afterRender: () => {
                    this.loaded.set(true);
                },
                afterUpdate: (chart: any) => {
                    if (this.legend()?.show) {
                        // pie charts only have 1 dataset
                        if (chart.config.type === 'pie' || chart.config.type === 'doughnut') {
                            const items = chart.data.labels.map((value, i) => ({
                                text: value,
                                fillStyle: chart.data.datasets[0].backgroundColor[i]
                            }));
                            this.legendItems.set(items);
                        } else {
                            const items = chart.data.datasets.map((dataset: any, i: number) => ({
                                text: dataset.label,
                                fillStyle: dataset.backgroundColor || dataset.borderColor,
                                strokeStyle: dataset.borderColor,
                                hidden: dataset.hidden || false,
                                datasetIndex: i
                            }));
                            this.legendItems.set(items);
                        }
                        this.flexColumn = this.testLegendPositions('bottom', 'top');
                    }
                },
            }],
        };
    }

    constructor(public app: AppService) {
        super();
        this.registerChartPlugins();

        this.app.theme$
            .pipe(takeUntil(this.destroyed))
            .subscribe(theme => {
                this.colors = THEME_GRAPH_PALETTES[get(theme.settings, 'graph.palette', 'default')] || THEME_GRAPH_PALETTES.default;
                this.axesLabelColor = theme.variables['--graph-label-font-color'] || '#333';
                this.axesLabelFont = theme.variables['--graph-label-font-family'] || 'Arial';
                try {
                    const rem = parseFloat(theme.variables['--graph-label-font-size'] || '1rem'); // believe it or not, but this works to get a float
                    this.axesLabelSize = parseFloat(window.getComputedStyle(document.documentElement).fontSize) * rem;
                } catch {
                    this.axesLabelSize = 10;
                }

                if (this.datasets()) {
                    this.updateGraphConfig();
                }
            });
    }

    ngOnChanges(changes: SimpleChanges) {
        if (Object.keys(changes).some(x => this.redrawChangesList.has(x))) {
            this.shouldRedrawChart = true;
        };
        this.updateGraphConfig();
    }

    updateGraph() {
        if (this.chart && this.shouldRedrawChart) {
            this.chart.destroy();
            this.chart = null;
            this.shouldRedrawChart = false;
        }

        const canvas = this.chartContext?.nativeElement;
        const existing = Chart.getChart(canvas);
        if (existing) existing.destroy();

        const ctx = canvas?.getContext('2d');
        if (ctx) {
            this.chart = new Chart(ctx, this.config);
        }
    }

    getDataset(item: Dataset, index: number): ChartDataSetsWithPlugins {
        return {
            borderWidth: 1.2,
            data: item.data.map((val: DataPoint) => val.raw),
            hidden: item.hidden,
            label: (typeof item.label === 'string') ? item.label : item.label && item.label.name,
            spanGaps: true,
            datalabels: {
                color: '#ffffff',
                backgroundColor: item.color || this.colors && this.colors[index] || 'rgb(47, 166, 212)',
            },
        };
    }

    handleCursorOnHover(elements: any[]) {
        const canvas = this.chartContext?.nativeElement;
        if (canvas) {
            canvas.style.cursor = (elements && elements.length > 0) ? 'pointer' : 'default';
        }
    }

    handlePointsSelectionEvent(event: Event) {
        const elements = this.chart.getElementsAtEventForMode(event as any, 'nearest', { intersect: true }, false);
        const element = elements[0];

        if (element) {
            this.onPointSelection.emit({
                pointIndex: element.index,
                datasetIndex: element.datasetIndex,
            });
        }
    }

    // copied and adapted from https://gist.github.com/tobyjsullivan/96d37ca0216adee20fa95fe1c3eb56ac
    abbreviateNumber(value: number): string {
        let newValue = value;
        const suffixes = ['', 'k', 'm', 'b', 't'];
        let suffixNum = 0;
        while (newValue >= 1000) {
            newValue /= 1000;
            suffixNum++;
        }
        let result = parseFloat(newValue.toPrecision(3)).toString();
        return result += suffixes[suffixNum];
    }


    testLegendPositions(position01: string, position02?: string): boolean {
        if (!this.legend()) { return false; }
        if (!position02) { return this.legend().position === position01; }
        return this.legend().position === position01 || this.legend().position === position02;
    }

    toggleLegend() {
        this.legendMinimized.update(value => !value);
    }

    registerChartPlugins() {
        Chart.register(
            BarController,
            BarElement,
            CategoryScale,
            LinearScale,
            LineController,
            LineElement,
            PointElement,
            LinearScale,
            PieController,
            ArcElement,
            Title,
            Tooltip,
            CategoryScale,
            ChartAnnotation,
            Filler
        );
    }

    ngOnDestroy(): void {
        if (this.chart) {
            this.chart.destroy();
            this.chart = null;
        }
        super.ngOnDestroy();
    }
    
    // add graph specific config here
    abstract updateGraphConfig(): void;

    // handle legend clicks per chart type
    abstract clickLegend(item: LegendItem, index: number): void;
}
